import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, parseApiResponse } from '@/api/core';

export interface AdminWebTokenRow {
  user_id: number;
  email: string | null;
  user_name: string | null;
  role_name: string | null;
  company_name: string | null;
  site_names: string[];
  origin: string;
  language: string;
  browser: string;
  os: string;
  user_agent: string;
  updated_at: string;
  ttl_seconds: number;
  is_latest: boolean;
  token: string;
}

interface AdminFcmTokensResponse {
  tokens: AdminWebTokenRow[];
}

export const adminFcmTokenKeys = {
  all: ['adminFcmTokens'] as const,
};

export function useAdminFcmTokens() {
  return useQuery({
    queryKey: adminFcmTokenKeys.all,
    queryFn: async (): Promise<AdminWebTokenRow[]> => {
      const res = await apiHelpers.get<AdminFcmTokensResponse>('fcmTokens.list');
      const parsed = parseApiResponse<AdminFcmTokensResponse>(res);
      if (parsed.error) {
        throw new Error(parsed.error.message || 'Unknown error');
      }
      return parsed.data?.tokens ?? [];
    },
    refetchInterval: 15000,
  });
}

/** 특정 사용자의 특정 토큰 삭제 */
export function useDeleteFcmToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { user_id: number; token: string }) => {
      const res = await apiHelpers.delete('fcmTokens.delete', undefined, {
        data: payload,
      });
      const parsed = parseApiResponse(res);
      if (parsed.error) {
        throw new Error(parsed.error.message || 'Unknown error');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminFcmTokenKeys.all });
    },
  });
}

/** 특정 사용자의 모든 웹 토큰 삭제 */
export function useDeleteUserFcmTokens() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { user_id: number }) => {
      const res = await apiHelpers.delete('fcmTokens.deleteUser', undefined, {
        data: payload,
      });
      const parsed = parseApiResponse(res);
      if (parsed.error) {
        throw new Error(parsed.error.message || 'Unknown error');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminFcmTokenKeys.all });
    },
  });
}
